import React from 'react';
import { Controller } from 'react-hook-form';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';
import { getErrorMessage as getErr } from '@/utils/form-validation/schemaHelper';
import ErrorMessage from '@/components/shared/ErrorMessage/ErrorMessage';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';

interface EmergencyContactFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: any;
    watch: any;
    errors?: Record<string, any>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
    // Flag especial para contexto PROFILE_UPDATE (wizard)
    isProfileUpdate?: boolean;
}

const EmergencyContactFieldset: React.FC<EmergencyContactFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full',
    isProfileUpdate = false
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext, isProfileUpdate: boolean = false) => {
        if (isProfileUpdate) {
            return {
                layout: 'compact' as const,
                fieldsetStyle: 'border p-4 rounded-md',
                legendStyle: 'text-sm font-medium px-1 text-gray-400',
                showRequiredIndicators: true
            };
        }

        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    layout: 'compact' as const,
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400',
                    showRequiredIndicators: false
                };
            default: // FULL_FORM
                return {
                    layout: 'full' as const,
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400',
                    showRequiredIndicators: false
                };
        }
    };

    const layoutConfig = getLayoutByContext(context, isProfileUpdate);
    const finalLayout = layout === 'compact' ? layoutConfig : {
        layout: 'full' as const,
        fieldsetStyle: 'border p-4 rounded-md',
        legendStyle: 'text-sm font-medium px-1 text-gray-400',
        showRequiredIndicators: false
    };

    // Para EmergencyContact, sempre mostrar os campos nos contextos suportados
    const hasVisibleFields = ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'].includes(context);

    if (!hasVisibleFields) {
        return null;
    }

    const getErrorMessage = (fieldPath: string) => getErr(errors, fieldPath);

    return (
        <fieldset className={finalLayout.fieldsetStyle}>
            <legend className={finalLayout.legendStyle}>Contato de Emergência</legend>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {/* Nome */}
                <div>
                    <label htmlFor="emergencyName" className="block text-sm font-medium text-gray-700">
                        Nome
                        {(collaboratorSchemaHelpers.isFieldRequired('emergencyContact', 'PROFILE_UPDATE') || finalLayout.showRequiredIndicators || isProfileUpdate) && <RequiredIndicator />}
                    </label>
                    <Controller
                        name="emergencyContact.name"
                        control={control}
                        render={({ field }) => (
                            <input
                                {...field}
                                id="emergencyName"
                                type="text"
                                className="input-primary w-full mt-1"
                                disabled={isSubmitting}
                            />
                        )}
                    />
                    <ErrorMessage errors={errors} fieldPath="emergencyContact.name" />
                </div>

                {/* Telefone */}
                <div>
                    <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700">
                        Telefone
                        {(collaboratorSchemaHelpers.isFieldRequired('emergencyContact', 'PROFILE_UPDATE') || finalLayout.showRequiredIndicators || isProfileUpdate) && <RequiredIndicator />}
                    </label>
                    <Controller
                        name="emergencyContact.phone"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value || ''}
                                onChange={(phone) => field.onChange(phone)}
                                defaultCountry="br"
                                inputProps={{
                                    id: 'emergencyPhone',
                                    name: 'emergencyPhone'
                                }}
                                inputClassName="input-primary w-full"
                                style={{
                                    '--react-international-phone-height': '42px',
                                    '--react-international-phone-border-radius': '0.375rem',
                                    '--react-international-phone-border-color': '#D1D5DB',
                                    '--react-international-phone-background-color': 'transparent',
                                    '--react-international-phone-text-color': '#111827'
                                } as React.CSSProperties}
                                countrySelectorStyleProps={{
                                    buttonContentWrapperClassName: 'px-2'
                                }}
                                disabled={isSubmitting}
                            />
                        )}
                    />
                    <ErrorMessage errors={errors} fieldPath="emergencyContact.phone" />
                </div>

                {/* Parentesco */}
                <div>
                    <label htmlFor="emergencyRelationship" className="block text-sm font-medium text-gray-700">
                        Parentesco
                        {(collaboratorSchemaHelpers.isFieldRequired('emergencyContact', 'PROFILE_UPDATE') || finalLayout.showRequiredIndicators || isProfileUpdate) && <RequiredIndicator />}
                    </label>
                    <Controller
                        name="emergencyContact.relationship"
                        control={control}
                        render={({ field }) => (
                            <input
                                {...field}
                                id="emergencyRelationship"
                                type="text"
                                className="input-primary w-full mt-1"
                                disabled={isSubmitting}
                            />
                        )}
                    />
                    <ErrorMessage errors={errors} fieldPath="emergencyContact.relationship" />
                </div>
            </div>
        </fieldset>
    );
};

export default EmergencyContactFieldset; 